import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { Palette, Language, Brightness6 } from "@mui/icons-material";
import PageHeader from "../components/common/PageHeader";
import ThemeSettings from "../components/common/ThemeSettings";
import { useTheme } from "../contexts/ThemeContext";

const Settings: React.FC = () => {
  const { actualTheme } = useTheme();
  const [language, setLanguage] = useState("en");

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value);
    // TODO: Implement language switching logic
    console.log("Language changed to:", event.target.value);
  };

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <PageHeader title="Settings" subtitle="Customize your app preferences" />

      <Stack spacing={3}>
        {/* Theme Settings */}
        <Card
          sx={{
            borderRadius: 3,
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(240, 246, 252, 0.08)"
                : "1px solid rgba(0, 0, 0, 0.06)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0, 0, 0, 0.3)"
                : "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Theme Section Header */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <Palette sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Appearance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customize the visual theme of the app
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Theme Settings Component */}
            <Box sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Brightness6 sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Theme Mode
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Currently using {actualTheme} theme
                  </Typography>
                </Box>
              </Stack>
              <ThemeSettings />
            </Box>
          </CardContent>
        </Card>

        {/* Language Settings */}
        <Card
          sx={{
            borderRadius: 3,
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(240, 246, 252, 0.08)"
                : "1px solid rgba(0, 0, 0, 0.06)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0, 0, 0, 0.3)"
                : "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Language Section Header */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                  }}
                >
                  <Language sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Language
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select your preferred language
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Language Selection */}
            <Box sx={{ p: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="language-select-label">Language</InputLabel>
                <Select
                  labelId="language-select-label"
                  value={language}
                  onChange={handleLanguageChange}
                  label="Language"
                  sx={{
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    },
                  }}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: "1.2em" }}>
                          {lang.flag}
                        </Typography>
                        <Typography>{lang.name}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Language changes will take effect after refreshing the page
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* App Info */}
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.02)",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Catalog App v1.0.0
          </Typography>
        </Paper>
      </Stack>
    </Container>
  );
};

export default Settings;
